<template>
  <div>
    <dashboard-header :text="$t('nav.dashboard')"/>

    <v-row>
      <v-col cols="4">
        <v-card
            elevation="0"
            :loading="pieChartLoading"
            class="card-border-10">

          <div>
            <div class="text-h6 pa-db">{{ $t("ui.alarmType") }}</div>
            <v-divider></v-divider>
            <div class="chart-d-wrapper mx-auto pa-db">
              <pie-chart v-if="pieChartData"
                         :chart-data="pieChartData"
                         :locale="locale"
              />
            </div>
          </div>

        </v-card>
      </v-col>
      <v-col cols="8">
        <v-card
            elevation="0"
            :loading="barChartLoading"
            class="card-border-10">
          <div>
            <div class="text-h6 pa-db"> {{ $t("ui.responseTimeByAlarmType") }}</div>
            <v-divider></v-divider>
            <div class="chart-top-wrapper pa-db">
              <bar-chart v-if="barChartData"
                         :chart-data="barChartData"
                         :locale="locale"
              />
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <div class="mt-6">
      <v-card
          elevation="0"
          :loading="combineChartLoading"
          class="card-border-10">
        <div>
          <div class="text-h6 pa-db">{{ $t("ui.callTheNurse") }}</div>
          <v-divider></v-divider>
          <div class="chart-combine-wrapper pa-db">
            <combine-bar-chart
                v-if="combineChartData"
                :chart-data="combineChartData"
                :locale="locale"
            />
          </div>

        </div>
      </v-card>
    </div>

    <div class="mt-6">
      <v-card
          :loading="statDataTableLoading"
          elevation="0"
          class="card-border-10">
        <div class="pa-10">
          <div class="d-flex">
            <div class="text-h6 font-weight-bold">{{ $t('sosCall.caption') }}</div>

            <div class="ms-auto mt-2 text-body-1"> {{ $t('ui.filterBy') }}:</div>

            <v-dialog
                v-model="dialog"
                persistent
                width="800px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    @click="openSelectDate"
                    class="ms-2 no-uppercase"
                    :color="getTextCalendarColor"
                    tile
                    elevation="0"
                    outlined
                    v-bind="attrs"
                    v-on="on">

                  {{ textStartDate }}

                  <img class="mx-2"
                       :src="require('@/assets/images/ic-arrow-calendar.svg')"/>

                  {{ textEndDate }}
                  <img class="ms-2" v-if="getDateData" @click.stop="onClearDate"
                    :src="require('@/assets/images/ic-close.svg')"/>
                  <img class="ms-2" v-else
                      :src="require('@/assets/images/ic-calendar.svg')"/>
                </v-btn>
              </template>

              <v-card>
                <v-card-title class="text-h6">
                  {{ $t('ui.selectTimePeriod') }}
                </v-card-title>

                <div class="d-flex mx-6">
                  <div class="w-50">
                    <div class="d-flex">
                      <div :class="'circle-border text-body-1 align-content-center ' + circleDateClass(pickStartDate)">
                        1
                      </div>
                      <div class="text-body-1 align-content-center mx-2"> {{ textPickStartDate }}</div>
                      <v-btn v-if="pickStartDate"
                             @click="pickStartDate = null"
                             class="align-content-center my-auto"
                             icon
                             small>
                        <v-icon>mdi-close-circle-outline</v-icon>
                      </v-btn>
                    </div>

                    <v-date-picker
                        class="mt-6"
                        color="blue darken-1"
                        no-title
                        full-width
                        :locale="locale==='en' ? 'en-us' : 'vi-vn'"
                        v-model="pickStartDate"
                        :min="minPickStartDate"
                        :max="maxPickStartDate"
                        scrollable>
                    </v-date-picker>
                  </div>
                  <div class="w-50">
                    <div class="d-flex">
                      <div
                          :class="'circle-border text-body-1 align-content-center ' + circleDateClass(pickEndDate)">
                        2
                      </div>
                      <div class="text-body-1 align-content-center mx-2">
                        {{ textPickEndDate }}
                      </div>
                      <v-btn v-if="pickEndDate"
                             @click="pickEndDate = null"
                             class="align-content-center my-auto"
                             icon
                             small>
                        <v-icon>mdi-close-circle-outline</v-icon>
                      </v-btn>
                    </div>
                    <v-date-picker
                        class="mt-6"
                        color="blue darken-1"
                        no-title
                        full-width
                        :min="minPickEndDate"
                        :max="maxPickEndDate"
                        :locale="locale==='en' ? 'en-us' : 'vi-vn'"
                        v-model="pickEndDate"
                        scrollable>
                    </v-date-picker>
                  </div>

                </div>

                <div class="d-flex pa-4">
                  <v-btn
                      outlined
                      elevation="0"
                      class="ms-auto no-uppercase"
                      @click="cancelSelectDate"
                  >
                    {{ $t("ui.cancel") }}
                  </v-btn>
                  <v-btn
                      class="ms-2 no-uppercase"
                      elevation="0"
                      @click="confirmSelectDate"
                      color="info"
                  >
                    {{ $t("ui.select") }}
                  </v-btn>
                </div>

              </v-card>
            </v-dialog>

            <v-btn
                elevation="0"
                @click="onClickDownloadReport"
                class="ms-2 no-uppercase"
                color="info">
              <img
                  :src="require('@/assets/images/ic-download.svg')"/>
              {{ $t("base.downloadExcel") }}
            </v-btn>

          </div>

          <v-data-table
              :loading="statDataTableLoading"
              class="home-data-table mt-10 text-body-1"
              :headers="statTableHeaders"
              :items="statDataTable"
              :items-per-page="perPage"
              :page.sync="currentPage"
              :options.sync="options"
              :server-items-length="totalRows"
              table-class="home-data-table mt-10"
              hide-default-footer
          >

            <template v-slot:item.time="{ item }">
              <div class="text-body-1">
                {{ formatDate(item.time, "DD/MM/YYYY") }}
              </div>
              <div class="text-body-2">
                {{ formatDate(item.time, "HH:mm") }}
              </div>
            </template>

            <template v-slot:item.departmentCall="{ item }">
              <div>
                {{ item.documentInfo.source }}
              </div>
              <div v-if="item.documentInfo.call_by">
                <img :src="require('@/assets/images/ic-mobile-border.svg')"/>
                {{ item.documentInfo.call_by.employeeName }}
              </div>
            </template>

            <template v-slot:item.sosType="{ item }">
              <div v-if="item.isCustomCall == 1">
                Báo chủ động
              </div>
              <div v-else-if="item.sosTypeInfo">
                {{ item.sosTypeInfo.name }}
              </div>
            </template>

            <template v-slot:item.sosContent="{ item }">
              <div v-if="item.SosCase">
                {{ item.SosCase.description }}
              </div>
            </template>

            <template v-slot:item.departmentCalled="{ item }">
              <v-col class="h-100">
                <element-confirm-state :item="item.documentInfo.departments1"
                                       :index="item.documentInfo.departments.length > 0? 1 : 0"
                                       :show-expand="item.documentInfo.departments.length > 0"
                                       show-index
                />

                <v-expand-transition>
                  <div v-if="item.documentInfo.departments1.expand">
                    <div
                        v-for="(item, index) in item.documentInfo.departments"
                        :key="'department' + item.name + index">
                      <v-divider></v-divider>
                      <element-confirm-state :item="item"
                                             :index="index + 2"
                                             show-index
                      />

                    </div>
                  </div>

                </v-expand-transition>

              </v-col>
            </template>

            <template v-slot:item.doctorCalled="{ item }">
              <v-col class="h-100">
                <element-confirm-state :item="item.documentInfo.callees1"
                                       :index="1"
                />

                <v-expand-transition>
                  <div v-if="item.documentInfo.departments1.expand">
                    <div
                        v-for="(item, index) in item.documentInfo.callees"
                        :key="'doctor' + item.name + index">
                      <v-divider></v-divider>
                      <element-confirm-state :item="item"
                                             :index="index + 2"
                      />

                    </div>
                  </div>

                </v-expand-transition>

              </v-col>
            </template>

            <template v-slot:item.audio="{ item }">
              <div v-if="item.fileId">
                <div v-if="item.fileInfo" class="d-flex align-center">
                  <v-btn icon small color="info" @click="playAudio(item)">
                    <v-icon>{{ item.playing ? 'mdi-pause' : 'mdi-play' }}</v-icon>
                  </v-btn>
                  <span class="text-caption grey--text ms-1" v-if="item.fileInfo.fileSize">
                    {{ (item.fileInfo.fileSize / 1024).toFixed(1) }} KB
                  </span>
                </div>
                <div v-else class="red--text text-caption">
                   <v-icon x-small color="red">mdi-clock-alert-outline</v-icon>
                   {{ $t("sosCall.expired") }}
                </div>
              </div>
              <div v-else class="grey--text text-caption">-</div>
            </template>

            <template slot="no-data">
              <div class="w-100 align-content-center" style="min-height: 500px">
                <div class="w-100 d-flex">
                  <img class="mx-auto" src="@/assets/images/ic-empty.svg" alt="empty"/>
                </div>
                <div class="text-center">{{ $t("base.noData") }}</div>
              </div>
            </template>

          </v-data-table>

          <div class="d-flex">
            <div class="ms-auto mt-3"> {{ paginationCurrentText }}</div>
            <b-pagination
                class="custom-pagination"
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                aria-controls="my-table">
            </b-pagination>
          </div>

        </div>
      </v-card>

    </div>

    <v-dialog
        v-model="downloadReportDialog"
        max-width="600px">
      <v-card>
        <div class="pa-8">
          <div class="d-flex">
            <div>
              <div class="text-h6">
                {{ $t("base.downloadExcel") }}
              </div>

              <div class="mt-5">
                <v-radio-group v-model="downloadOptionDate">
                  <v-radio value="all">
                    <template v-slot:label>
                      <div class="mt-2">{{ $t("ui.allData") }}</div>
                    </template>
                  </v-radio>

                  <v-radio value="option">
                    <template v-slot:label>
                      <div class="mt-2">{{ $t("ui.option") }}</div>
                    </template>
                  </v-radio>
                </v-radio-group>

                <div v-if="downloadOptionDate==='option'" class="d-flex">
                  <div class="mt-2">
                    {{ $t("ui.selectTimePeriod") }}:
                  </div>

                  <v-btn
                      @click="onClickOptionDateReport"
                      class="ms-2 no-uppercase"
                      :color="getTextCalendarColor"
                      tile
                      elevation="0"
                      outlined
                  >

                    {{ textDownloadStartDate }}

                    <img class="mx-2"
                         :src="require('@/assets/images/ic-arrow-calendar.svg')"/>

                    {{ textDownloadEndDate }}
                    <img class="ms-2"
                         :src="require('@/assets/images/ic-calendar.svg')"/>
                  </v-btn>

                </div>

              </div>
            </div>
          </div>

        </div>

        <div class="pa-8 d-flex">
          <v-btn
              outlined
              elevation="0"
              class="ms-auto no-uppercase"
              @click="onClickDownloadReportCancel"
          >
            {{ $t("ui.cancel") }}
          </v-btn>
          <v-btn
              class="ms-2 no-uppercase"
              elevation="0"
              @click="onClickDownloadReportConfirm"
              :loading="downloadLoading"
              :disabled="downloadLoading"
              color="info"
          >
            {{ $t("base.downloadExcel") }}
          </v-btn>
        </div>

      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import i18n from "../../lang/i18n";
import {API2} from "../../api";
import moment from "moment";
import CombineBarChart from "./CombineBarChart";
import BarChart from "./BarChart";
import PieChart from "./PieChart";
import {mapState} from "vuex";
import ElementConfirmState from "./ElementConfirmState";
import {TYPE_FORMAT_TIME} from "../../config/constant";
import DashboardHeader from "../component/DashboardHeader";
import mixin from "../../mixin";
import storage, {STORAGE} from "../../helper/storage";

export default {
  components: {DashboardHeader, ElementConfirmState, CombineBarChart, BarChart, PieChart},
  mixins: [mixin],
  data() {
    return {
      // table
      expand: false,
      statDataTable: [],
      options: {},
      optionParams: {page: 1, itemsPerPage: 10},
      totalRows: 10,
      perPage: 10,
      currentPage: 1,
      // filter table
      startDate: null,
      endDate: null,
      pickStartDate: null,
      pickEndDate: null,
      downloadStartDate: null,
      downloadEndDate: null,

      dialog: false,
      pieChartData: null,
      barChartData: null,
      combineChartData: null,

      downloadReportDialog: false,
      downloadOptionDate: 'all',
      downloadLoading: false,
      pieChartLoading: false,
      barChartLoading: false,
      combineChartLoading: false,
      statDataTableLoading: false,
    };
  },
  computed: {
    ...mapState(['locale']),
    paginationCurrentText() {
      let last = this.currentPage * this.perPage
      let first = last - this.perPage + 1
      let total = this.totalRows
      last = last > total ? total : last
      return `${first} - ${last} / ${total}`
    },
    statTableHeaders() {
      return [
        {value: "time", align: 'start', text: i18n.t("sosCall.time"), sortable: false,},
        {value: "departmentCall", text: i18n.t("sosCall.sourceName"), sortable: false,},
        {value: "sosType", text: i18n.t("sosCall.sosType"), sortable: false,},
        {value: "sosContent", text: i18n.t("nav.sosMessage"), sortable: false,},
        {value: "departmentCalled", text: i18n.t("ui.departmentResponse"), sortable: false,},
        {value: "doctorCalled", text: i18n.t("sosCall.doctorCalled"), sortable: false,},
        {value: "audio", text: i18n.t("sosCall.audio"), sortable: false,},
      ];
    },
    getTextCalendarColor() {
      // If a date range is selected, use black; otherwise keep grey placeholder color
      const hasDate = this.downloadReportDialog
        ? (this.downloadStartDate || this.downloadEndDate)
        : (this.startDate || this.endDate)
      return hasDate ? '#000000' : '#BFBFBF'
    },
    getDateData() {
      return !!this.startDate || !!this.endDate;
    },
    textStartDate() {
      return this.getTextDateFormat(this.startDate, "ui.startDate")
    },
    textEndDate() {
      return this.getTextDateFormat(this.endDate, "ui.endDate")
    },
    textDownloadStartDate() {
      return this.getTextDateFormat(this.downloadStartDate, "ui.startDate")
    },
    textDownloadEndDate() {
      return this.getTextDateFormat(this.downloadEndDate, "ui.endDate")
    },
    textPickStartDate() {
      return this.getTextDateFormat(this.pickStartDate, "ui.startDate")
    },
    textPickEndDate() {
      return this.getTextDateFormat(this.pickEndDate, "ui.endDate")
    },
    minPickStartDate() {
      if (this.pickEndDate) {
        let endDate = new Date(this.pickEndDate)
        endDate.setMonth(endDate.getMonth() - 3)
        return this.formatDate(endDate, TYPE_FORMAT_TIME.YYYY_MM_DD)
      }
      return null
    },
    maxPickStartDate() {
      if (this.pickEndDate) return this.pickEndDate
      return this.formatDate(new Date(), TYPE_FORMAT_TIME.YYYY_MM_DD)
    },
    minPickEndDate() {
      return this.pickStartDate
    },
    maxPickEndDate() {
      let now = new Date()
      if (this.pickStartDate) {
        let startDate = new Date(this.pickStartDate)
        startDate.setMonth(startDate.getMonth() + 3)
        let lower = startDate.getTime() < now.getTime() ? startDate : now
        return this.formatDate(lower, TYPE_FORMAT_TIME.YYYY_MM_DD)
      }
      return this.formatDate(now, TYPE_FORMAT_TIME.YYYY_MM_DD)
    },
  },
  mounted() {
    this.setDefaultDatePicker();
    this.getPieChartData();
    this.getBarChartData();
    this.getCallNurseChartData();
    this.getStatDataTable()
  },
  methods: {
    setDefaultDatePicker() {
      const now = new Date()
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
      this.startDate = this.formatDate(firstDay, TYPE_FORMAT_TIME.YYYY_MM_DD)
      this.endDate = this.formatDate(now, TYPE_FORMAT_TIME.YYYY_MM_DD)
      this.optionParams.startDate = this.startDate
      this.optionParams.endDate = this.endDate
    },
    onClickDownloadReport() {
      this.downloadReportDialog = true
      this.downloadStartDate = null
      this.downloadEndDate = null
    },
    onClickOptionDateReport() {
      this.dialog = true
    },
    onClickDownloadReportCancel() {
      this.downloadReportDialog = false
    },
    async onClickDownloadReportConfirm() {
      // Frontend validation: Check if there's any data to export
      if (this.downloadOptionDate === 'all' && this.statDataTable.length === 0) {
        this.downloadReportDialog = false
        this.showToast("warning", i18n.t("ui.anErrorOccurred"), i18n.t("ui.noDataToExport"))
        return
      }

      let params = this.downloadOptionDate === 'all' ? {} : {
        startDate: this.downloadStartDate,
        endDate: this.downloadEndDate,
      }

      try {
        this.downloadLoading = true
        let res = await this.get(API2.SOS_CALL_EXCEL, params, "downloadLoading")
        this.downloadReportDialog = false
        if (res && res.message) {
          this.showToast("success", i18n.t("ui.yourRequestHasBeenProcessed"), res.message)
        }
      } catch (error) {
        this.downloadReportDialog = false
        // Handle "no data" error from backend
        if (error?.message && error.message.includes("no data")) {
          this.showToast("warning", i18n.t("ui.anErrorOccurred"), i18n.t("ui.noDataToExport"))
        } else {
          this.showToast("danger", i18n.t("ui.anErrorOccurred"), error?.message || "Download failed")
        }
      } finally {
        this.downloadLoading = false
      }
    },
    async getPieChartData() {
      const params = { page: this.optionParams.page, itemsPerPage: this.optionParams.itemsPerPage }
      let res = await this.get(API2.SOS_CALL_STATISTIC_BY_TYPE, params, "pieChartLoading");
      if (!res) return
      this.pieChartData = {
        data: res.datasets[0].data,
        en: res.labels.map(t => t.nameEng),
        vi: res.labels.map(t => t.name),
        colors: res.datasets[0].backgroundColor,
      }
    },
    async getBarChartData() {
      const params = { page: this.optionParams.page, itemsPerPage: this.optionParams.itemsPerPage }
      let res = await this.get(API2.SOS_CALL_DETAIL_STATISTIC_BY_TYPE, params, "barChartLoading");
      if (!res) return
      // console.log("getBarChartData", res)
      this.barChartData = {
        data: res.datasets.map(d => d.data),
        en: res.labels.map(t => t.nameEng),
        vi: res.labels.map(t => t.name),
        legendEn: res.datasets.map(d => d.labelEn),
        legendVi: res.datasets.map(d => d.labelVi),
        colors: res.datasets.map(d => d.backgroundColor),
      }
    },
    async getCallNurseChartData() {
      let res = await this.get(API2.NURSE_CASE_GET_NURSE, {hospitalId: 1}, "combineChartLoading");
      if (!res) return

      this.combineChartData = {
        legend1Vi: res.labelHospitalVi,
        legend1En: res.labelHospitalEn,
        legend2Vi: res.labelDepartmentVi,
        legend2En: res.labelDepartmentEn,
        data1: Array.apply(null, Array(3)).map((val, index) => res.departmentData.map(row => row[index])),
        data2: res.hospitalData,
        vi: res.departmentNameVi,
        en: res.departmentNameEn,
        colors1: res.colorBar,
        colors2: res.colorAverage,
      }
    },
    onSortOption() {
      this.optionParams = {
        sortBy: this.options.sortBy[0],
        orderBy: this.options.sortDesc[0] ? "DESC" : "ASC",
        page: this.currentPage,
        itemsPerPage: this.options.itemsPerPage,
        startDate: this.startDate,
        endDate: this.endDate
      }
      this.getStatDataTable()
    },
    async getStatDataTable() {
      let res = await this.get(API2.SOS_CALL, this.optionParams, "statDataTableLoading")
      if (!res) return
      this.totalRows = res.total
      this.statDataTable = res.data
        .filter((item) => {
          const call_by = item.documentInfo?.call_by;
          return !(call_by?.isDepartmentAccount === true || call_by?.isDepartmentEmployeeAccount === true);
        })
        .map((item) => {
          if (item.documentInfo.departments.length > 0) {
            item.documentInfo.departments1 = item.documentInfo.departments.shift();
            item.documentInfo.departments1.expand = false
          }
          // Filter callees to exclude Department Accounts and Department Employee Accounts
          item.documentInfo.callees = (item.documentInfo.callees || []).filter(c =>
            c.isDepartmentAccount !== true && c.isDepartmentEmployeeAccount !== true
          );
          if (item.documentInfo.callees.length > 0) {
            item.documentInfo.callees1 = item.documentInfo.callees.shift();
            item.documentInfo.callees1.expand = false
          }
          item.playing = false;
          item.audio = null;
          return item
        })
    },
    async playAudio(item) {
      // Stop other playing audio if any
      this.statDataTable.forEach(row => {
        if (row !== item && row.playing && row.audio) {
          row.audio.pause();
          row.playing = false;
        }
      });

      if (item.playing) {
        if (item.audio) item.audio.pause();
        item.playing = false;
        return;
      }

      if (!item.audio) {
        const token = storage.getItem(STORAGE.TOKEN);
        const url = `${API2.FILE}/${item.fileId}?token=${token}`;
        item.audio = new Audio(url);
        item.audio.onended = () => {
          item.playing = false;
        };
        item.audio.onerror = () => {
          item.playing = false;
          this.showToast("danger", i18n.t("ui.anErrorOccurred"), "Phát âm thanh thất bại");
        };
      }

      try {
        await item.audio.play();
        item.playing = true;
      } catch (e) {
        this.showToast("danger", i18n.t("ui.anErrorOccurred"), "Trình duyệt từ chối phát tự động hoặc lỗi file");
      }
    },
    getTextDateFormat(date, key) {
      if (date) return this.formatDate(date, TYPE_FORMAT_TIME.DD_MM_YYYY)
      else return i18n.t(key)
    },
    circleDateClass(date) {
      return date ? 'circle-border-selected' : ''
    },
    openSelectDate() {
      this.pickStartDate = this.startDate
      this.pickEndDate = this.endDate
    },
    cancelSelectDate() {
      this.dialog = false
    },
    confirmSelectDate() {
      this.dialog = false
      if (this.downloadReportDialog) {
        this.downloadStartDate = this.pickStartDate
        this.downloadEndDate = this.pickEndDate
      } else {
        this.startDate = this.pickStartDate
        this.endDate = this.pickEndDate
        this.optionParams.startDate = this.startDate
        this.optionParams.endDate = this.endDate
        this.getStatDataTable()
      }
    },
    onClearDate() {
      if (this.downloadReportDialog) {
        this.downloadStartDate = null
        this.downloadEndDate = null
      } else {
        this.startDate = null
        this.endDate = null
        this.optionParams.startDate = this.startDate
        this.optionParams.endDate = this.endDate
        this.getStatDataTable()
      }
    },
  },
  watch: {
    options: {
      handler() {
        this.onSortOption()
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.chart-top-wrapper {
  height: 30rem
}

.chart-d-wrapper {
  height: 30rem;
  max-width: 320px;
}

.chart-combine-wrapper {
  height: 36rem
}

.custom-pagination >>> .page-item {
  margin: 5px;
}

.custom-pagination >>> .page-item .page-link {
  color: #000000;
}

.custom-pagination >>> .active > .page-link {
  background: unset;
  color: #1890FF;
  border: 1px solid #1890FF;
}

.custom-pagination >>> .disabled > .page-link {
  color: #D9D9D9;
}

.circle-border {
  width: 40px;
  height: 40px;
  text-align: center;
  border-radius: 26px;
  border: 1px solid #BFBFBF;
  color: #BFBFBF;
}

.circle-border-selected {
  background: #1890FF;
  border: 1px solid #1890FF;
  color: #ffffff;
}

</style>
